import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TestPlanService } from '../../../services/test-plan.service';
import { TestCaseService } from '../../../services/test-case.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { TestPlan } from '../../../models/test-plan.model';
import { TestCase, TestExecutionStatus } from '../../../models/test-case.model';
import { PlanStatusPillComponent } from '../plan-status-pill/plan-status-pill.component';
import { ResultBadgeKind, ResultStatusBadgeComponent } from '../result-status-badge/result-status-badge.component';

interface ReportRow {
  planPublicId: string | null;
  planId: string;
  testCaseKey: string;
  testCaseId: string;
  summary: string;
  status: ResultBadgeKind;
  requirementLabel: string | null;
}

@Component({
  selector: 'app-test-plan-report',
  imports: [CommonModule, RouterLink, TranslatePipe, MatIcon, PlanStatusPillComponent, ResultStatusBadgeComponent],
  templateUrl: './test-plan-report.component.html',
  styleUrl: './test-plan-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPlanReportComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private planService = inject(TestPlanService);
  private testCaseService = inject(TestCaseService);
  private destroyRef = inject(DestroyRef);
  private projectRoleService = inject(ProjectRoleService);

  readonly projectId: string;
  readonly planId: string;
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly plan = signal<TestPlan | null>(null);
  readonly rows = signal<ReportRow[]>([]);
  readonly search = signal('');
  readonly isReadOnly = computed(
    () => this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin(),
  );

  readonly filteredRows = computed(() => {
    const term = this.search().toLowerCase().trim();
    if (!term) return this.rows();
    return this.rows().filter((r) => (r.testCaseKey + ' ' + r.summary).toLowerCase().includes(term));
  });

  readonly totalTestCases = computed(() => this.plan()?.progress.total ?? 0);
  readonly passedCount = computed(() => this.plan()?.progress.passed ?? 0);
  readonly failedCount = computed(() => this.plan()?.progress.failed ?? 0);
  readonly inProgressCount = computed(() => this.plan()?.progress.inProgress ?? 0);
  readonly openCount = computed(() => this.plan()?.progress.open ?? 0);

  readonly completedPercent = computed(() => {
    const total = this.totalTestCases() || 1;
    return Math.round(((this.passedCount() + this.failedCount()) / total) * 100);
  });

  readonly segments = computed(() => {
    const total = this.totalTestCases() || 1;
    const rawSegments: { kind: ResultBadgeKind; value: number }[] = [
      { kind: 'passed', value: this.passedCount() },
      { kind: 'failed', value: this.failedCount() },
      { kind: 'in-progress', value: this.inProgressCount() },
      { kind: 'open', value: this.openCount() },
    ];
    return rawSegments.filter((s) => s.value > 0).map((s) => ({ ...s, percent: Math.round((s.value / total) * 100) }));
  });

  constructor() {
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.planId = this.route.snapshot.paramMap.get('planId') ?? '';
    this.loadReport();
  }

  private loadReport(): void {
    if (!this.planId) {
      this.loadError.set(true);
      this.loading.set(false);
      return;
    }
    forkJoin({
      plan: this.planService.getById(this.projectId, this.planId).pipe(catchError(() => of(null))),
      testCases: this.testCaseService.list(this.projectId).pipe(catchError(() => of([] as TestCase[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ plan, testCases }) => {
          if (!plan) {
            this.loadError.set(true);
            this.loading.set(false);
            return;
          }
          this.plan.set(plan);
          const byId = new Map(testCases.map((tc) => [tc.id, tc]));
          const assigned = plan.testCaseIds.map((id) => byId.get(id)).filter((tc): tc is TestCase => !!tc);
          this.rows.set(assigned.map((tc) => this.toRow(plan, tc)));
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private toRow(plan: TestPlan, tc: TestCase): ReportRow {
    return {
      planPublicId: plan.publicId,
      planId: plan.id,
      testCaseKey: tc.key ?? tc.id.substring(0, 8),
      testCaseId: tc.id,
      summary: tc.title,
      status: this.mapStatus(tc.lastExecutionStatus ?? null),
      requirementLabel: tc.requirementLabel ?? null,
    };
  }

  private mapStatus(status: TestExecutionStatus | null): ResultBadgeKind {
    switch (status) {
      case 'PASSED':
        return 'passed';
      case 'FAILED':
        return 'failed';
      case 'RUNNING':
        return 'in-progress';
      default:
        return 'open';
    }
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  onBack(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans']);
  }

  onOpenResults(row: ReportRow): void {
    this.router.navigate(['/projects', this.projectId, 'test-results', 'history', row.testCaseId]);
  }

  onOpenPlan(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans', this.planId, 'edit']);
  }
}
